/**
 * Safe HTML strings. Every interpolated value is escaped unless it is already an {@link Html} (built by `html`).
 * Components render to strings so they can be tested in Node and mounted by whatever the app uses.
 */

/** A string of trusted markup. */
export class Html {
  readonly value: string;
  constructor(value: string) {
    this.value = value;
  }
  toString(): string {
    return this.value;
  }
}

const ESCAPES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Escape text for an element body or a quoted attribute. */
export function escape(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

export type Part = Html | string | number | boolean | null | undefined | readonly Part[];

function render(part: Part): string {
  if (part === null || part === undefined || part === false || part === true) return "";
  if (part instanceof Html) return part.value;
  if (Array.isArray(part)) return part.map(render).join("");
  return escape(String(part));
}

/** Tagged template: `html\`<b>${name}</b>\``. Arrays are joined; null, undefined and booleans render nothing. */
export function html(strings: TemplateStringsArray, ...parts: Part[]): Html {
  let out = strings[0] ?? "";
  for (let i = 0; i < parts.length; i++) out += render(parts[i]) + (strings[i + 1] ?? "");
  return new Html(out);
}

/** Join pieces with a separator, escaping each. */
export function join(parts: readonly Part[], separator: Part = ""): Html {
  return new Html(parts.map(render).join(render(separator)));
}

/** Mark a string as trusted markup. Use only for markup this package or the caller built. */
export function raw(value: string): Html {
  return new Html(value);
}

/** Render a part to a string (escaped unless already Html). */
export function toHtml(part: Part): Html {
  return new Html(render(part));
}

/** Space-joined class names, skipping falsy entries. */
export function cx(...names: ReadonlyArray<string | false | null | undefined>): string {
  return names.filter((n): n is string => typeof n === "string" && n.length > 0).join(" ");
}

/** Attributes as a leading-space string: `{ id: "a", disabled: true }` gives ` id="a" disabled`. Undefined and false are skipped. */
export function attrs(map: Readonly<Record<string, string | number | boolean | null | undefined>>): Html {
  let out = "";
  for (const [key, value] of Object.entries(map)) {
    if (value === undefined || value === null || value === false) continue;
    out += value === true ? ` ${key}` : ` ${key}="${escape(String(value))}"`;
  }
  return new Html(out);
}
